import { v4 as uuidv4 } from 'uuid';

import { WARNING_SOURCES } from '../../constants/WarningsConstants';
import { translate } from '../../form-designer/utils/form-manager.utils';
import { fieldsValidationProps } from '../components/FieldsTable';
import { relationsValidationProps } from '../components/RelationsTable';
import { resolvedFieldsValidationProps } from '../components/ResolvedFieldsTable';
import {
  ENTITY_FIELD_STATUS_PUBLISHED,
  ENTITY_RUNTIME_TABLE,
  TABLE_FIELDS_CONTROL_TYPE,
  TABLE_RELATIONS_CONTROL_TYPE,
  TABLE_RESOLVED_FIELDS_CONTROL_TYPE,
} from '../constants/entity-designer.constants';

export const isFieldPropUnique = ({ items = [], prop, value }) => {
  const itemsByProp = items.filter((item) => item[prop]?.toUpperCase() === value?.toUpperCase());
  return itemsByProp.length === 1;
};

export const validateRequired = ({ item, validationDetails, validationErrors, translations }) => {
  if (validationDetails.required && !item[validationDetails?.prop]) {
    validationErrors.push({
      errorId: uuidv4(),
      instanceId: item.rowId,
      controlInfo: {
        prop: validationDetails?.prop,
      },
      message: `${validationDetails.errorLabel} ${translate(translations, 'is required')}.`,
      source: WARNING_SOURCES.VALIDATION,
    });
  }
};

export const validateExpression = ({ item, validationDetails, validationErrors }) => {
  if (!validationDetails.validation?.expression || !item[validationDetails?.prop]) {
    return;
  }

  const isValid = new RegExp(validationDetails.validation.expression).test(
    item[validationDetails?.prop]
  );

  !isValid &&
    validationErrors.push({
      errorId: uuidv4(),
      instanceId: item.rowId,
      controlInfo: {
        prop: validationDetails?.prop,
      },
      message: validationDetails.validation.message,
      source: WARNING_SOURCES.VALIDATION,
    });
};

export const validateUnique = ({
  items,
  item,
  validationDetails,
  validationErrors,
  translations,
}) => {
  if (
    validationDetails.unique &&
    item[validationDetails?.prop] &&
    !isFieldPropUnique({
      items,
      prop: validationDetails?.prop,
      value: item[validationDetails?.prop],
    })
  ) {
    validationErrors.push({
      errorId: uuidv4(),
      instanceId: item.rowId,
      controlInfo: {
        prop: validationDetails?.prop,
      },
      message: `${validationDetails.errorLabel} ${translate(translations, 'should be unique')}.`,
      source: WARNING_SOURCES.VALIDATION,
    });
  }
};

export const validateComputedFormula = ({ field, validationErrors, translations }) => {
  const isComputedFormulaValid =
    (field.IsComputed && field.ComputedFormula) || (!field.IsComputed && !field.ComputedFormula);

  if (isComputedFormulaValid) {
    return;
  }

  const errorMessageLabel = 'Computed Value Formula';

  validationErrors.push({
    errorId: uuidv4(),
    instanceId: field.rowId,
    controlInfo: {
      prop: 'ComputedFormula',
    },
    message: `${errorMessageLabel} ${translate(
      translations,
      field.IsComputed ? 'is required' : 'should be empty'
    )}.`,
    source: WARNING_SOURCES.VALIDATION,
  });
};

export const validateControls = ({
  items = [],
  validationForTable,
  controlsValidationProps,
  validationErrors = [],
  translations,
}) => {
  items.forEach((item) => {
    if (validationForTable === TABLE_FIELDS_CONTROL_TYPE) {
      validateComputedFormula({ field: item, validationErrors, translations });
    }

    if (
      validationForTable === TABLE_FIELDS_CONTROL_TYPE &&
      item.Status === ENTITY_FIELD_STATUS_PUBLISHED
    ) {
      return;
    }

    controlsValidationProps.forEach((validationDetails) => {
      validateRequired({ item, validationDetails, validationErrors, translations });
      validateExpression({ item, validationDetails, validationErrors, translations });
      validateUnique({ items, item, validationDetails, validationErrors, translations });
    });
  });
};

export const validateEntityOnSave = ({
  fields,
  relations,
  resolvedFields,
  translations,
  runtimeTableValue,
}) => {
  const validationErrors = [];

  validateControls({
    items: fields,
    validationForTable: TABLE_FIELDS_CONTROL_TYPE,
    controlsValidationProps: fieldsValidationProps,
    validationErrors,
    translations,
  });

  validateControls({
    items: relations,
    validationForTable: TABLE_RELATIONS_CONTROL_TYPE,
    controlsValidationProps: relationsValidationProps,
    validationErrors,
    translations,
  });

  validateControls({
    items: resolvedFields,
    validationForTable: TABLE_RESOLVED_FIELDS_CONTROL_TYPE,
    controlsValidationProps: resolvedFieldsValidationProps,
    validationErrors,
    translations,
  });

  if (!runtimeTableValue.length) {
    validationErrors.push({
      errorId: uuidv4(),
      instanceId: ENTITY_RUNTIME_TABLE,
      controlInfo: {
        prop: ENTITY_RUNTIME_TABLE,
      },
      message: `Runtime Table ${translate(translations, 'is required')}.`,
      source: WARNING_SOURCES.VALIDATION,
    });
  }

  return validationErrors;
};

export const getRightValidationErrorMessage = (label, translations) =>
  `${label} ${translate(translations, 'is invalid')}.`;
